<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Message from 'primevue/message'
import Select from 'primevue/select'
import ProgressBar from 'primevue/progressbar'
import FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload'
import Papa from 'papaparse'
import { useProductStore } from 'shared/products/stores/productStore'
import { useCreateProduct, type CreateProduct } from '../api/createProduct'
import { ProductType } from 'shared/products/domain/productType'
import { Unit, unitStrings } from 'shared/common/domain/bulkUnits'
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore'
import { useTranslations } from 'shared/translations/composables/useTranslations'
import { usePrimeVue } from 'primevue/config'
import { useLanguageStore } from 'shared/translations/stores/languageStore'

const visible = defineModel('visible', { type: Boolean, default: false })

const translations = useTranslations()
const t = translations.value.products.importDialog

const languageStore = useLanguageStore();
const primevue = usePrimeVue();

function setPrimeVueLanguage() {
  if (!primevue.config.locale) return;
  primevue.config.locale = translations.value.primeVueLocale;
}

setPrimeVueLanguage();
watch(() => languageStore.language.code, () => setPrimeVueLanguage()
)

const selectedFileName = ref('')
const selectedFile = ref<File | null>(null)
const isLoading = ref(false)
const errorMessage = ref('')
const missingHeaders = ref<string[]>([])
const rowsPerPage = ref(100)
const fu = ref()

const featureFlagStore = useFeatureFlagStore()

const productStore = useProductStore()
const createProduct = useCreateProduct()

interface RowData {
  [key: string]: any
  __id: number;
  __errors?: Record<string, string>
  __hasError?: boolean
  __importStatus?: "Success" | "Pending" | "Error"
}

const emit = defineEmits(['imported'])

// ---------------------------------------------------------------------
// Product type selection — drives which schema/columns are active.
// Only types enabled via feature flags are selectable.
// ---------------------------------------------------------------------
const productTypeLabels = computed<Partial<Record<ProductType, string>>>(() => ({
  [ProductType.Log]: translations.value.productType(ProductType.Log),
  [ProductType.Coil]: translations.value.productType(ProductType.Coil),
  [ProductType.Bulk]: translations.value.productType(ProductType.Bulk),
}))

function productTypeLabel(type: ProductType): string {
  return productTypeLabels.value[type] ?? String(type)
}

const allowedTypes = computed<ProductType[]>(() => {
  const types: ProductType[] = []
  if (featureFlagStore.isLogYardEnabled()) types.push(ProductType.Log)
  if (featureFlagStore.isCoilEnabled()) types.push(ProductType.Coil)
  if (featureFlagStore.isBulkEnabled()) types.push(ProductType.Bulk)
  return types
})

const typeOptions = computed(() =>
  allowedTypes.value.map((type) => ({ label: productTypeLabel(type), value: type })),
)

const selectedType = ref<ProductType | null>(null)

// If a flag is disabled while a now-disallowed type is selected, clear it.
watch(allowedTypes, (types) => {
  if (selectedType.value && !types.includes(selectedType.value)) {
    selectedType.value = null
  }
})

// Changing the type re-validates against the new schema. If a file was
// already parsed, re-parse the same File with the new column set —
// activeSchema (used inside parseCsvFile) already reflects the new type
// by the time this callback runs.
watch(selectedType, () => {
  if (selectedFile.value) {
    parseCsvFile(selectedFile.value)
  }
})

// ---------------------------------------------------------------------
// Expected format. Edit these to match the real spec — each entry
// describes one expected CSV column and how to validate it.
// ---------------------------------------------------------------------
type FieldType = 'string' | 'number' | 'integer' | 'boolean' | 'hexcolor' | 'enum'

interface ColumnSchema {
  field: string
  header: string
  required: boolean
  type: FieldType
  options?: string[] // only used when type === 'enum'
  min?: number // only used when type === 'number' — rejects values below this
}

// Fields shared by every product type
const commonSchema = computed<ColumnSchema[]>(() => [
  { field: 'product_name', header: t.columnProductName, required: true, type: 'string' },
  { field: 'product_description', header: t.columnDescription, required: false, type: 'string' },
  { field: 'external_code', header: t.columnExternalCode, required: false, type: 'string' },
  { field: 'color', header: t.columnColor, required: false, type: 'hexcolor' },
  { field: 'quality', header: t.columnQuality, required: false, type: 'string' },
])

const schemasByType = computed<Partial<Record<ProductType, ColumnSchema[]>>>(() => ({
  [ProductType.Log]: [
    ...commonSchema.value,
    { field: 'species', header: t.columnSpecies, required: false, type: 'string' },
    { field: 'logs_per_square_meter', header: t.columnLogsPerSquareMeter, required: true, type: 'number', min: 0.1 },
    { field: 'min_diameter', header: t.columnMinDiameter, required: false, type: 'number' },
    { field: 'max_diameter', header: t.columnMaxDiameter, required: false, type: 'number' },
    { field: 'max_length', header: t.columnMaxLength, required: false, type: 'number' },
    { field: 'min_length', header: t.columnMinLength, required: false, type: 'number' },
    { field: 'average_cylinder_volume', header: t.columnAverageCylinderVolume, required: false, type: 'number' },
    { field: 'average_physical_volume', header: t.columnAveragePhysicalVolume, required: false, type: 'number' },
    { field: 'is_sorted', header: t.columnSorted, required: true, type: 'boolean' },
    { field: 'log_sorter_name', header: t.columnLogSorter, required: false, type: 'string' },
    { field: 'saw_line_name', header: t.columnSawLine, required: false, type: 'string' },
  ],
  [ProductType.Coil]: [
    ...commonSchema.value,
    { field: 'diameter', header: t.columnDiameter, required: true, type: 'number' },
    { field: 'depth', header: t.columnDepth, required: true, type: 'number' },
  ],
  [ProductType.Bulk]: [
    ...commonSchema.value,
    { field: 'density', header: t.columnDensity, required: true, type: 'number', min: 0.1 },
    { field: 'unit', header: t.columnUnit, required: true, type: 'enum', options: unitStrings},
  ],
}));

// The schema (and therefore the visible columns) currently in play,
// based on the Select above.
const activeSchema = computed<ColumnSchema[]>(() =>
  selectedType.value ? schemasByType.value[selectedType.value] ?? [] : [],
);

// ---------------------------------------------------------------------
// Parsed data — kept in two layers: the raw parsed rows, and a computed
// validated version that re-runs automatically if activeSchema changes.
// ---------------------------------------------------------------------
const rawRows = ref<RowData[]>([])

const existingNameSet = computed(
  () => new Set((productStore.products ?? []).map((p: any) => String(p.name).trim().toLowerCase())),
)

const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

function validateValue(schema: ColumnSchema, row: RowData): string | null {
  const rawValue = row[schema.field]
  const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue
  const isEmpty = value === undefined || value === null || value === ''

  if (isEmpty) {
    return schema.required ? `${schema.header} ${t.validationRequired}` : null
  }

  switch (schema.type) {
    case 'number':
      const num = Number(value)
      if (Number.isNaN(num)) return `${schema.header} ${t.validationMustBeNumber}`
      if (schema.min !== undefined && num < schema.min) {
        return `${schema.header} ${t.validationMustBeAtLeast} ${schema.min}.`
      }
      return null
    case 'integer':
      return Number.isInteger(Number(value)) ? null : `${schema.header} ${t.validationMustBeInteger}`
    case 'boolean':
      return ['TRUE', 'FALSE'].includes(String(value).toUpperCase())
        ? null
        : `${schema.header} ${t.validationMustBeBoolean}`
    case 'hexcolor':
      return HEX_COLOR_RE.test(String(value)) ? null : `${schema.header} ${t.validationMustBeHexColor}`
    case 'enum':
      return schema.options?.includes(String(value))
        ? null
        : `${schema.header} ${t.validationMustBeOneOf} ${schema.options?.join(', ')}.`
    case 'string':
    default:
      return null
  }
}

function validateRow(row: RowData, schema: ColumnSchema[]): RowData {
  const errors: Record<string, string> = {}

  for (const fieldSchema of schema) {
    const message = validateValue(fieldSchema, row)
    if (message) errors[fieldSchema.field] = message
  }

  return { ...row, __errors: errors, __hasError: Object.keys(errors).length > 0, __importStatus: row.__importStatus }
}

// Second pass over the whole parsed file: flags product_name as invalid
// if it's duplicated elsewhere in the same file, or already exists in
// the product store.
function applyUniquenessValidation(inputRows: RowData[]): RowData[] {
  const nameCounts = new Map<string, number>()
  for (const row of inputRows) {
    const name = String(row.product_name ?? '').trim().toLowerCase()
    if (!name) continue
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1)
  }

  return inputRows.map((row) => {
    const rawName = String(row.product_name ?? '').trim()
    if (!rawName) return row // already flagged by the required check
    if(row.__importStatus) return row

    const name = rawName.toLowerCase()
    const errors = { ...row.__errors }

    if ((nameCounts.get(name) ?? 0) > 1) {
      errors.product_name = t.duplicateProductNameInFile
    } else if (existingNameSet.value.has(name)) {
      errors.product_name = t.productNameAlreadyExists
    }

    return { ...row, __errors: errors, __hasError: Object.keys(errors).length > 0 }
  })
}

// Recomputes automatically whenever rawRows or the selected type changes.
const rows = computed<RowData[]>(() => {
  if (rawRows.value.length === 0) return []
  const validated = rawRows.value.map((row) => validateRow(row, activeSchema.value))
  return applyUniquenessValidation(validated)
})

const invalidRowCount = computed(() => rows.value.filter((r) => r.__hasError).length)

// ---------------------------------------------------------------------
// File handling
// ---------------------------------------------------------------------
function onUpload(event: FileUploadUploaderEvent) {
  if (!selectedType.value) {
    errorMessage.value = t.selectTypeBeforeFile
    return
  }

  const files = Array.isArray(event.files) ? event.files : [event.files]
  const file = files[0]
  if (!file) return

  selectedFileName.value = file.name
  selectedFile.value = file
  parseCsvFile(file)
}

function parseCsvFile(file: File) {
  isLoading.value = true
  errorMessage.value = ''
  missingHeaders.value = []
  rawRows.value = []

  Papa.parse<RowData>(toRaw(file), {
    header: true,
    skipEmptyLines: true,
    // worker: true,
    complete: (results) => {
      isLoading.value = false

      if (results.errors.length > 0) {
        errorMessage.value = `${t.failedToParseCsvPrefix} ${results.errors[0]?.message}`
        console.error(results.errors)
        return
      }

      const foundHeaders = results.meta.fields ?? []
      missingHeaders.value = activeSchema.value
        .filter((s) => s.required && !foundHeaders.includes(s.field))
        .map((s) => s.header)

      rawRows.value = results.data.map((row, index) => ({
        ...row,
        __id: index,
      }))
    },
    error: (err) => {
      isLoading.value = false
      errorMessage.value = t.failedToParseCsvFile
      console.error(err)
    },
  })
}

function fileImport() {
  fu.value.upload()
  importSummary.value = null;
}

function rowClass(data: RowData) {
  return {
    'csv-row-invalid': data.__hasError && data.__importStatus === undefined,
    'csv-row-import-error': !!importErrorsByName.value[data.product_name] && data.__importStatus === "Error",
  }
}

// ---------------------------------------------------------------------
// Import — sends every row without validation errors to the API,
// one at a time, tracking progress and per-row failures as it goes.
// ---------------------------------------------------------------------
const isImporting = ref(false)
const importProgress = ref(0) // 0-100
const importErrorsByName = ref<Record<string, string>>({})
const importSummary = ref<{ success: number; failed: number } | null>(null)

const importAbortController = ref<AbortController | null>(null)
const importAborted = ref(false)

const importableCount = computed(() => rows.value.filter((r) => !r.__hasError).length)
const canImport = computed(
  () =>
    !!selectedType.value &&
    missingHeaders.value.length === 0 &&
    importableCount.value > 0 &&
    !isImporting.value &&
    importSummary.value === null,
)
const hasImportAttempt = computed(() => isImporting.value || importSummary.value !== null)

function toNullableString(value: unknown): string | null {
  const str = value === undefined || value === null ? '' : String(value).trim()
  return str === '' ? null : str
}

function toNullableNumber(value: unknown): number | null {
  const str = toNullableString(value)
  return str === null ? null : Number(str)
}

function toBoolean(value: unknown): boolean {
  return String(value ?? '').trim().toUpperCase() === 'TRUE'
}

// Downloads a CSV containing just the header row for the selected type,
// so users have a starting point that matches the expected columns.
function downloadTemplate() {
  if (!selectedType.value) return

  const header = Papa.unparse({ fields: activeSchema.value.map((c) => c.field), data: [] })
  const blob = new Blob(['\ufeff' + header], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = `${productTypeLabel(selectedType.value)}_import_template.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function rowToCreateProduct(row: RowData, type: ProductType): CreateProduct {
  const product: CreateProduct = {
    name: String(row.product_name ?? '').trim(),
    description: toNullableString(row.product_description) ?? '',
    type,
    color: toNullableString(row.color),
    externalCode: toNullableString(row.external_code),
    quality: toNullableString(row.quality),
    logData: null,
    coilData: null,
    bulkData: null,
    dimensionData: null,
    productGroupId: null,
  }

  if (type === ProductType.Log) {
    product.logData = {
      logsPerSquareMeter: toNullableNumber(row.logs_per_square_meter) ?? 1,
      minDiameter: toNullableNumber(row.min_diameter),
      maxDiameter: toNullableNumber(row.max_diameter),
      maxLength: toNullableNumber(row.max_length),
      minLength: toNullableNumber(row.min_length),
      averageCylinderVolume: toNullableNumber(row.average_cylinder_volume),
      averagePhysicalVolume: toNullableNumber(row.average_physical_volume),
      isSorted: toBoolean(row.is_sorted),
      species: toNullableString(row.species),
      logSorterName: toNullableString(row.log_sorter_name),
      sawLineName: toNullableString(row.saw_line_name),
    }
  }

  if (type === ProductType.Coil) {
    product.coilData = {
      diameter: toNullableNumber(row.diameter),
      depth: toNullableNumber(row.depth),
    }
  }

  if (type === ProductType.Bulk) {
    product.bulkData = {
      unit: Unit[row.unit as keyof typeof Unit],
      density: toNullableNumber(row.density) ?? 1,
    }
  }

  return product
}

async function importValidRows() {
  if (!selectedType.value) return

  const type = selectedType.value
  const importable = rows.value.filter((r) => !r.__hasError)
  if (importable.length === 0) return

  isImporting.value = true
  importProgress.value = 0
  importErrorsByName.value = {}
  importSummary.value = null
  importAborted.value = false

  const controller = new AbortController()
  importAbortController.value = controller

  let success = 0
  let failed = 0

  try {
    for (let i = 0; i < importable.length; i++) {
      // Don't start another import after Abort was pressed.
      if (controller.signal.aborted) {
        importAborted.value = true
        break
      }

      const row = importable[i]
      if (!row) continue

      const product = rowToCreateProduct(row, type)

      const rawRow = rawRows.value.find((r) => r.__id === row.__id)
      if (rawRow) {
        rawRow.__importStatus = 'Pending'
      }

      const response = await createProduct.execute({ entity: product })

      // Abort may have been pressed while the request was in flight.
      // Let that request finish, but don't start another one.
      if (controller.signal.aborted) {
        importAborted.value = true

        // The current request actually completed, so record its result.
        if (response.ok) {
          success++
          if (rawRow) {
            rawRow.__importStatus = 'Success'
          }
        } else {
          failed++
          if (rawRow) {
            rawRow.__importStatus = 'Error'
          }

          importErrorsByName.value[product.name] =
            response.details?.title ?? t.importFailedFallback
        }

        importProgress.value = Math.round(
          ((i + 1) / importable.length) * 100,
        )

        break
      }

      if (response.ok) {
        success++

        if (rawRow) {
          rawRow.__importStatus = 'Success'
        }
      } else {
        failed++

        if (rawRow) {
          rawRow.__importStatus = 'Error'
        }

        importErrorsByName.value[product.name] =
          response.details?.title ?? t.importFailedFallback
      }

      importProgress.value = Math.round(
        ((i + 1) / importable.length) * 100,
      )
    }

    importSummary.value = { success, failed }
    emit('imported', {
      type,
      success,
      failed,
      aborted: importAborted.value,
    })
  } finally {
    isImporting.value = false
    importAbortController.value = null
  }
}

function abortImport() {
  importAbortController.value?.abort()
}

function show() {
  visible.value = true
  selectedType.value = null
  selectedFileName.value = ''
  selectedFile.value = null
  rawRows.value = []
  errorMessage.value = ''
  missingHeaders.value = []
  isImporting.value = false
  importProgress.value = 0
  importErrorsByName.value = {}
  importSummary.value = null
  importAborted.value = false
  importAbortController.value = null
  fu.value?.clear()
}

defineExpose({
  show
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :maximizable="true"
    :closable="!isImporting"
    :header="t.title"
    :style="{ width: '75vw', height: '80vh' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    :content-style="{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }"
  >
    <div class="flex flex-col gap-4 flex-1 min-h-0">
      <!-- Product type -->
      <div class="flex items-center gap-3">
        <label for="csv-import-type" class="font-medium">{{ t.productType }}</label>
        <Select
          id="csv-import-type"
          v-model="selectedType"
          :options="typeOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="t.selectType"
          class="w-14rem"
          :disabled="isImporting"
        />
        <Button
          type="button"
          severity="secondary"
          text
          icon="pi pi-download"
          :label="t.downloadTemplate"
          :disabled="!selectedType || isImporting"
          @click="downloadTemplate"
        />
      </div>

      <!-- File picker -->
      <div class="flex justify-between">
        <div class="flex flex-wrap items-center gap-3">
          <FileUpload
            ref="fu"
            mode="basic"
            :chooseLabel="t.choose"
            name="demo[]"
            accept=".csv"
            :multiple="false"
            :maxFileSize="50000000" 
            :disabled="!selectedType || isImporting"
            customUpload
            @uploader="onUpload($event)"
          />
        </div>
        <Button
          type="button"
          severity="secondary"
          :disabled="!selectedType || isImporting"
          @click="fileImport"
        >
          {{ t.upload }}
        </Button>
      </div>

      <span v-if="selectedFileName" class="text-sm text-color-secondary">
        {{ t.uploadedFilePrefix }} {{ selectedFileName }}
      </span>

      <!-- Parse error -->
      <Message v-if="errorMessage" severity="error" :closable="false">
        {{ errorMessage }}
      </Message>

      <!-- Missing required columns -->
      <Message v-if="missingHeaders.length > 0" severity="error" :closable="false">
        {{ t.missingRequiredColumns }} {{ missingHeaders.join(', ') }}
      </Message>

      <!-- Row-validation summary -->
      <Message v-else-if="rows.length > 0 && invalidRowCount > 0" severity="warn" :closable="false">
        {{ invalidRowCount }} {{ t.invalidRowsOf }} {{ rows.length }} {{ t.invalidRowsHaveErrors }}
        <i class="pi pi-exclamation-triangle" style="color: #ff6f00" />
        {{ t.hoverIconForCellDetails }}
      </Message>
      <Message v-else-if="importSummary === null && rows.length > 0" severity="success" :closable="false">
        {{ t.allProductsValidPrefix }} {{ rows.length }} {{ t.allProductsValidSuffix }}
      </Message>

      <!-- Import progress -->
      <ProgressBar v-if="isImporting" :value="importProgress" :showValue="true" />

      <!-- Import summary -->
      <Message v-if="importSummary" :severity="importAborted || importSummary.failed > 0 ? 'warn' : 'success'"
        :closable="false">
        <template v-if="importAborted">{{ t.importAborted }}</template>
        {{ t.imported }} {{ importSummary.success }} {{ t.productsWord }}

        <template v-if="importSummary.failed > 0">
          {{ importSummary.failed }} {{ t.summaryFailed }}
          <i class="pi pi-times-circle" style="color: #ef4444" />
          {{ t.hoverIconForStatusDetails }}
        </template>
      </Message>

      <!-- DataTable preview -->
      <DataTable
        v-if="rows.length > 0"
        :value="rows"
        paginator
        v-model:rows="rowsPerPage"
        :rowsPerPageOptions="[25, 50, 100, 250, 500]"
        scrollable
        scrollHeight="flex"
        :loading="isLoading"
        :rowClass="rowClass"
        :emptyMessage="t.noDataLoaded"
        class="p-datatable-sm flex-1 min-h-0"
      >
        <Column v-if="hasImportAttempt" :header="t.status" style="width: 3.5rem">
          <template #body="{ data }">
            <i v-if="data.__importStatus === 'Success'" class="pi pi-check-circle" style="color: #22c55e" />
            <i v-else-if="data.__importStatus === 'Pending'" class="pi pi-spinner animate-spin text-primary-400"  />
            <i v-else-if="importErrorsByName[data.product_name] && data.__importStatus === 'Error'"
              class="pi pi-times-circle"
              style="color: #ef4444"
              :title="importErrorsByName[data.product_name]"
            />
          </template>
        </Column>
        <Column
          v-for="col in activeSchema"
          :key="col.field"
          :field="col.field"
          :header="col.header + (col.required ? ' *' : '')"
          sortable
        >
          <template #body="{ data }">
            <span>{{ data[col.field] }}</span>
            <i
              v-if="data.__importStatus === undefined && data.__errors?.[col.field]"
              class="pi pi-exclamation-triangle csv-cell-error-icon"
              :title="data.__errors[col.field]"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <template #footer>
      <Button :label="t.close" severity="secondary" text :disabled="isImporting" @click="visible = false" />

      <Button v-if="!isImporting" :label="t.import" :loading="isImporting" :disabled="!canImport" @click="importValidRows" />
      <Button v-else :label="t.abort" :loading="true" :disabled="false" severity="danger" icon="pi pi-stop" @click="abortImport" />
    </template>
  </Dialog>
</template>

<style scoped>
/* Light mode */
:deep(tr.csv-row-invalid) {
  background-color: #fff3cd !important;
}

:deep(tr.csv-row-import-error) {
  background-color: #f8d7da !important;
}

.csv-cell-error-icon {
  color: #ff6f00;
  margin-left: 0.4rem;
  cursor: help;
}
</style>

<style>
/* Dark mode overrides — unscoped on purpose: html.p-dark is a global
   ancestor that this component doesn't render, so it can't be scoped. */
html.p-dark tr.csv-row-invalid {
  background-color: #3a3119 !important;
}

html.p-dark tr.csv-row-import-error {
  background-color: #411c1c !important;
}

html.p-dark .csv-cell-error-icon {
  color: #ff9800;
}
</style>